import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Car, Fuel, Gauge, Palette, Phone, Filter, Search,
  ChevronLeft, ChevronRight, X, ZoomIn, Share2, Sparkles
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CarGallery = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('all');
  const [selectedVehicleType, setSelectedVehicleType] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxCar, setLightboxCar] = useState(null);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true); // Start visible

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await axios.get(`${API}/cars/`);
        setCars(response.data);
      } catch (error) {
        console.error('Error fetching cars:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  const makes = [
    'Alfa Romeo', 'Audi', 'Bentley', 'BMW', 'BYD', 'Chery', 'Chevrolet',
    'Ferrari', 'Ford', 'Genesis', 'Honda', 'Hyundai', 'Jaecoo', 'Jaguar',
    'Jeep', 'Kia', 'Lamborghini', 'Land Rover', 'Lexus', 'Lucid', 'Maserati',
    'Mazda', 'Mercedes-Benz', 'Mini', 'Mitsubishi', 'NIO', 'Nissan', 'Peugeot',
    'Polestar', 'Porsche', 'Range Rover', 'Renault', 'Rivian', 'Rolls-Royce',
    'Seat', 'Skoda', 'Subaru', 'Suzuki', 'Tesla', 'Toyota', 'Volkswagen',
    'Volvo', 'Xpeng', 'Zeekr'
  ];

  const motorcycleMakes = [
    'Aprilia', 'BMW Motorrad', 'Benelli', 'Ducati', 'Harley-Davidson', 'Honda',
    'Indian', 'Kawasaki', 'KTM', 'Moto Guzzi', 'MV Agusta', 'Royal Enfield',
    'Suzuki', 'Triumph', 'Yamaha', 'Zontes'
  ];

  const availableMakes = selectedVehicleType === 'motorcycle' 
    ? motorcycleMakes 
    : selectedVehicleType === 'car'
    ? makes
    : [...new Set([...makes, ...motorcycleMakes])].sort();
  
  const priceRanges = [
    { value: 'all', label: 'כל הטווחים' },
    { value: '0-100000', label: 'עד ₪100,000' },
    { value: '100000-200000', label: '₪100,000 - ₪200,000' },
    { value: '200000-300000', label: '₪200,000 - ₪300,000' },
    { value: '300000+', label: 'מעל ₪300,000' }
  ];

  const filteredCars = cars.filter(car => {
    const matchesSearch = car.make?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         car.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         car.color?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVehicleType = selectedVehicleType === 'all' || 
                               (car.vehicleType || 'car') === selectedVehicleType;
    const matchesMake = selectedMake === 'all' || 
                       car.make?.trim().toLowerCase() === selectedMake.toLowerCase();
    
    let matchesPrice = true;
    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(p => p.replace('+', ''));
      matchesPrice = max ? (car.price >= parseInt(min) && car.price <= parseInt(max)) : car.price >= parseInt(min);
    }
    
    return matchesSearch && matchesVehicleType && matchesMake && matchesPrice;
  });

  const formatPrice = (price) => new Intl.NumberFormat('he-IL', {
    style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(price);

  const handleContactAboutCar = (car) => {
    const message = `שלום, אני מעוניין/ת ברכב ${car.make} ${car.model} שנת ${car.year}. אשמח לקבל פרטים נוספים.`;
    const phoneNumber = "0587019669".replace(/[^0-9]/g, '');
    window.open(`https://wa.me/972${phoneNumber.substring(1)}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const nextImage = (carId, totalImages) => {
    setCurrentImageIndex(prev => ({ ...prev, [carId]: ((prev[carId] || 0) + 1) % totalImages }));
  };

  const prevImage = (carId, totalImages) => {
    setCurrentImageIndex(prev => ({ ...prev, [carId]: ((prev[carId] || 0) - 1 + totalImages) % totalImages }));
  };

  const openLightbox = (car, imageIndex = 0) => {
    setLightboxCar(car);
    setLightboxImageIndex(imageIndex);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxCar(null);
    document.body.style.overflow = 'unset';
  };

  if (loading) {
    return (
      <section className="py-32 bg-luxury-black">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}>
            <Sparkles className="w-12 h-12 text-luxury-gold mx-auto mb-4" />
            <p className="text-luxury-gold text-xl">טוען רכבים...</p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="cars" ref={sectionRef} className="py-24 bg-luxury-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-luxury-gold" />
            <span className="text-luxury-gold text-sm font-medium tracking-[0.3em] uppercase">המלאי שלנו</span>
            <div className="h-px w-12 bg-luxury-gold" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-luxury-text mb-6">
            מבחר <span className="gold-shimmer">הרכבים</span> שלנו
          </h2>
          <p className="text-lg text-luxury-muted max-w-2xl mx-auto">
            רכבים לכל הסוגים ולכל הכיסים - מקיה חסכונית ועד BMW מפוארת
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div 
          className="glass rounded-none p-6 mb-12 border border-luxury-border"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <Filter className="w-5 h-5 text-luxury-gold" />
            <h3 className="text-lg font-semibold text-luxury-text">סינון רכבים</h3>
            <div className="flex-1 h-px bg-luxury-border" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Vehicle Type */}
            <div className="md:hidden">
              <select
                value={selectedVehicleType}
                onChange={(e) => { setSelectedVehicleType(e.target.value); setSelectedMake('all'); }}
                className="w-full h-12 px-4 bg-luxury-surface border border-luxury-border text-luxury-text focus:border-luxury-gold focus:outline-none transition-colors"
              >
                <option value="all">הכל</option>
                <option value="car">מכוניות</option>
                <option value="motorcycle">אופנועים</option>
              </select>
            </div>
            <div className="hidden md:block">
              <Select value={selectedVehicleType} onValueChange={(v) => { setSelectedVehicleType(v); setSelectedMake('all'); }}>
                <SelectTrigger className="bg-luxury-surface border-luxury-border text-luxury-text h-12 rounded-none focus:border-luxury-gold">
                  <SelectValue placeholder="סוג רכב" />
                </SelectTrigger>
                <SelectContent className="bg-luxury-surface border-luxury-border rounded-none">
                  <SelectItem value="all" className="text-luxury-text">הכל</SelectItem>
                  <SelectItem value="car" className="text-luxury-text">מכוניות</SelectItem>
                  <SelectItem value="motorcycle" className="text-luxury-text">אופנועים</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-luxury-muted w-5 h-5" />
              <Input
                placeholder="חפש רכב..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-12 h-12 bg-luxury-surface border-luxury-border text-luxury-text placeholder-luxury-muted focus:border-luxury-gold rounded-none"
              />
            </div>

            {/* Make */}
            <div className="md:hidden">
              <select
                value={selectedMake}
                onChange={(e) => setSelectedMake(e.target.value)}
                className="w-full h-12 px-4 bg-luxury-surface border border-luxury-border text-luxury-text focus:border-luxury-gold focus:outline-none transition-colors"
              >
                <option value="all">כל היצרנים</option>
                {availableMakes.map(make => <option key={make} value={make}>{make}</option>)}
              </select>
            </div>
            <div className="hidden md:block">
              <Select value={selectedMake} onValueChange={setSelectedMake}>
                <SelectTrigger className="bg-luxury-surface border-luxury-border text-luxury-text h-12 rounded-none focus:border-luxury-gold">
                  <SelectValue placeholder="בחר יצרן" />
                </SelectTrigger>
                <SelectContent className="bg-luxury-surface border-luxury-border rounded-none max-h-64">
                  <SelectItem value="all" className="text-luxury-text">כל היצרנים</SelectItem>
                  {availableMakes.map(make => <SelectItem key={make} value={make} className="text-luxury-text">{make}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Price */}
            <div className="md:hidden">
              <select
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className="w-full h-12 px-4 bg-luxury-surface border border-luxury-border text-luxury-text focus:border-luxury-gold focus:outline-none transition-colors"
              >
                {priceRanges.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="hidden md:block">
              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger className="bg-luxury-surface border-luxury-border text-luxury-text h-12 rounded-none focus:border-luxury-gold">
                  <SelectValue placeholder="טווח מחירים" />
                </SelectTrigger>
                <SelectContent className="bg-luxury-surface border-luxury-border rounded-none">
                  {priceRanges.map(r => <SelectItem key={r.value} value={r.value} className="text-luxury-text">{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {/* Cars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {filteredCars.map((car, index) => {
              const currentIndex = currentImageIndex[car.id] || 0;
              const hasMultipleImages = car.images && car.images.length > 1;
              
              return (
                <motion.div
                  key={car.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="luxury-card group"
                >
                  {/* Image Container */}
                  <div className="relative overflow-hidden cursor-pointer" onClick={() => openLightbox(car, currentIndex)}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <motion.img
                        src={car.images?.[currentIndex] || car.images?.[0] || '/placeholder-car.jpg'}
                        alt={`${car.make} ${car.model}`}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.7 }}
                      />
                    </div>
                    
                    {/* Overlays */}
                    <div className="absolute inset-0 bg-gradient-to-t from-luxury-black via-transparent to-transparent opacity-60" />
                    
                    {/* Hover Zoom Icon */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-luxury-black/40">
                      <div className="w-14 h-14 rounded-full bg-luxury-gold/90 flex items-center justify-center">
                        <ZoomIn className="w-6 h-6 text-luxury-black" />
                      </div>
                    </div>

                    {/* Navigation Arrows */}
                    {hasMultipleImages && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); prevImage(car.id, car.images.length); }}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-luxury-black/60 hover:bg-luxury-gold text-white hover:text-luxury-black transition-all duration-300 opacity-0 group-hover:opacity-100"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); nextImage(car.id, car.images.length); }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-luxury-black/60 hover:bg-luxury-gold text-white hover:text-luxury-black transition-all duration-300 opacity-0 group-hover:opacity-100"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        {/* Dots */}
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {car.images.map((_, i) => (
                            <button
                              key={i}
                              onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => ({...prev, [car.id]: i})); }}
                              className={`h-1 transition-all ${i === currentIndex ? 'w-6 bg-luxury-gold' : 'w-2 bg-white/50 hover:bg-white/80'}`}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {/* Badges */}
                    <Badge className="absolute top-4 right-4 bg-luxury-gold text-luxury-black font-bold rounded-none px-3 py-1">
                      {car.condition || 'משומש'}
                    </Badge>

                    {/* Share Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const text = `${car.make} ${car.model} - שנת ${car.year}\n${formatPrice(car.price)}\nJ CLASS MOTORS`;
                        if (navigator.share) {
                          navigator.share({ title: `${car.make} ${car.model}`, text, url: window.location.href });
                        } else {
                          navigator.clipboard.writeText(text).then(() => alert('✅ הועתק!'));
                        }
                      }}
                      className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center bg-luxury-black/60 hover:bg-luxury-gold text-white hover:text-luxury-black transition-all duration-300"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>

                    {/* Price Tag */}
                    <div className="absolute bottom-4 right-4 bg-luxury-black/80 backdrop-blur-sm px-4 py-2">
                      <p className="text-2xl font-bold text-luxury-gold">{formatPrice(car.price)}</p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-luxury-text mb-1">{car.make} {car.model}</h3>
                      <p className="text-luxury-gold font-medium">שנת {car.year}</p>
                    </div>

                    {/* Specs Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-luxury-border">
                      <div className="flex items-center gap-2">
                        <Gauge className="w-4 h-4 text-luxury-gold" />
                        <span className="text-luxury-muted text-sm">{car.mileage?.toLocaleString()} ק"מ</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Fuel className="w-4 h-4 text-luxury-gold" />
                        <span className="text-luxury-muted text-sm">{car.fuelType}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Palette className="w-4 h-4 text-luxury-gold" />
                        <span className="text-luxury-muted text-sm">{car.color}</span>
                      </div>
                      {car.hand && (
                        <div className="flex items-center gap-2">
                          <span className="text-luxury-gold text-sm">יד</span>
                          <span className="text-luxury-muted text-sm">{car.hand}</span>
                        </div>
                      )}
                    </div>

                    {/* Engine Info */}
                    <div className="mb-4 p-3 bg-luxury-surface-light/50 border border-luxury-border">
                      <p className="text-luxury-text font-medium text-sm">{car.engine}</p>
                      <p className="text-luxury-muted text-sm">{car.transmission} • {car.power}</p>
                    </div>

                    {/* CTA Button */}
                    <motion.button
                      onClick={() => handleContactAboutCar(car)}
                      className="btn-luxury w-full flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Phone className="w-4 h-4" />
                      <span>פנה בנוגע לרכב</span>
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* No Results */}
        {filteredCars.length === 0 && (
          <motion.div 
            className="text-center py-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Car className="w-20 h-20 text-luxury-border mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-luxury-text mb-3">לא נמצאו רכבים</h3>
            <p className="text-luxury-muted">נסה לשנות את המסננים או את מונחי החיפוש</p>
          </motion.div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && lightboxCar && (
          <motion.div 
            className="fixed inset-0 z-[9999] bg-luxury-black/98 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <button onClick={closeLightbox} className="absolute top-6 right-6 z-[10000] w-12 h-12 flex items-center justify-center bg-luxury-surface hover:bg-luxury-gold text-luxury-text hover:text-luxury-black transition-all">
              <X className="w-6 h-6" />
            </button>

            <div className="absolute top-6 left-6 z-[10000] bg-luxury-surface px-4 py-2">
              <span className="text-luxury-text font-medium">{lightboxImageIndex + 1} / {lightboxCar.images?.length || 1}</span>
            </div>

            <div className="relative w-full h-full flex items-center justify-center px-4 md:px-20" onClick={(e) => e.stopPropagation()}>
              {lightboxCar.images?.length > 1 && (
                <>
                  <button onClick={() => setLightboxImageIndex(p => (p - 1 + lightboxCar.images.length) % lightboxCar.images.length)} className="absolute left-4 md:left-8 z-[10000] w-14 h-14 flex items-center justify-center bg-luxury-surface hover:bg-luxury-gold text-luxury-text hover:text-luxury-black transition-all">
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button onClick={() => setLightboxImageIndex(p => (p + 1) % lightboxCar.images.length)} className="absolute right-4 md:right-8 z-[10000] w-14 h-14 flex items-center justify-center bg-luxury-surface hover:bg-luxury-gold text-luxury-text hover:text-luxury-black transition-all">
                    <ChevronRight className="w-8 h-8" />
                  </button>
                </>
              )}

              <div className="max-w-5xl max-h-[75vh] flex flex-col items-center">
                <motion.img
                  key={lightboxImageIndex}
                  src={lightboxCar.images?.[lightboxImageIndex]}
                  alt={`${lightboxCar.make} ${lightboxCar.model}`}
                  className="max-w-full max-h-[65vh] object-contain"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
                
                {lightboxCar.images?.length > 1 && (
                  <div className="flex gap-2 mt-4 overflow-x-auto max-w-full px-2 pb-2">
                    {lightboxCar.images.map((img, i) => (
                      <button key={i} onClick={() => setLightboxImageIndex(i)} className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 border-2 transition-all ${i === lightboxImageIndex ? 'border-luxury-gold opacity-100' : 'border-luxury-border opacity-50 hover:opacity-100'}`}>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-luxury-black to-transparent p-8">
              <div className="max-w-7xl mx-auto flex justify-between items-end">
                <div>
                  <h3 className="text-3xl font-bold text-luxury-text mb-2">{lightboxCar.make} {lightboxCar.model}</h3>
                  <p className="text-luxury-gold text-xl">{formatPrice(lightboxCar.price)} • שנת {lightboxCar.year}</p>
                </div>
                <Button onClick={() => handleContactAboutCar(lightboxCar)} className="btn-luxury">
                  <Phone className="w-4 h-4 ml-2" />
                  צור קשר
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default CarGallery;
